import React, { useState } from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Box,
  useToast,
  Collapse,
} from "@chakra-ui/react";
import useSWR from "swr";
import axios from "axios";
import { useTranslation } from "next-i18next";

import { useUser } from "@/hooks/useUser";
import { Accreditation } from "@/types/accreditation";
import { error as errorToast } from "@/utils/toast";

type Props = {
  eventId?: number;
};

const WithdrawnAccreditationStack = ({
  eventId,
}: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const [session] = useUser();
  const toast = useToast();
  const [closingAlerts, setClosingAlerts] = useState<Set<number>>(new Set());
  const {
    data: alerts,
    error,
    mutate,
  } = useSWR<Accreditation[]>(
    `/accreditation/withdrawn/unread${eventId ? `?eventId=${eventId}` : ""}`
  );

  const handleClose = async (accreditationId: number) => {
    setClosingAlerts((prev) => new Set([...prev, accreditationId]));

    setTimeout(async () => {
      try {
        await axios.post(
          `/accreditation/${accreditationId}/acknowledge-withdrawal`
        );

        mutate();
      } catch (err) {
        toast(
          errorToast({
            description: err,
            duration: 5000,
            isClosable: true,
          })
        );
      } finally {
        setClosingAlerts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(accreditationId);
          return newSet;
        });
      }
    }, 200);
  };

  const isOrganizer =
    session?.currentRole?.role === "admin" ||
    session?.currentRole?.role === "mod";

  if (!isOrganizer || error || !alerts || alerts.length === 0) return null;

  const maxVisibleOffset = Math.min(alerts.length, 3);
  const stackHeight = 60 + (maxVisibleOffset - 1) * 12;

  return (
    <Box position="relative" mb={6} height={`${stackHeight}px`}>
      {alerts?.map((accreditation, index) => {
        const offsetIndex = Math.min(index, 2);
        const isClosing = closingAlerts.has(accreditation.id);

        return (
          <Collapse
            key={`withdraw-alert-${accreditation?.id}-${accreditation?.event?.id}`}
            in={!isClosing}
            animateOpacity
          >
            <Alert
              status="warning"
              variant="left-accent"
              position="absolute"
              top={`${offsetIndex * 12}px`}
              width={`${100 - offsetIndex * 5}%`}
              left="50%"
              transform="translateX(-50%)"
              zIndex={alerts.length - index}
              boxShadow="md"
              borderRadius="md"
              bg="orange.50"
              borderColor="orange.200"
              borderWidth="1px"
              opacity={index > 2 ? 0.7 : 1}
              transition="all 0.3s ease-in-out"
            >
              <AlertIcon />

              <Box flex="1">
                <AlertTitle>{t("events.withdrawAlertTitle", {})}</AlertTitle>
                <AlertDescription>
                  {t("events.withdrawAlertDescription", {
                    user: `${accreditation?.user?.firstName} ${accreditation?.user?.lastName} (${accreditation?.user?.email})`,
                  })}
                </AlertDescription>
              </Box>

              <CloseButton
                position="absolute"
                right="8px"
                top="8px"
                onClick={() => handleClose(accreditation?.id)}
              />
            </Alert>
          </Collapse>
        );
      })}
    </Box>
  );
};

export default WithdrawnAccreditationStack;
